import { useId, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { commitItemEdits } from "../../../api/content/changeSetsApi";
import { getItemVersion } from "../../../api/content/itemsApi";
import type { ItemChange, Review } from "../../../api/content/reviewDecoders";
import ErrorNotice from "../../../errors/ErrorNotice";
import type { UiError } from "../../../errors/uiError";
import { useLoad } from "../../../shared/loading/useLoad";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import SourceView from "../../../ui/content/source/SourceView";
import LoadStatus from "../../../ui/feedback/LoadStatus";

/**
 * A curator's raw Turtle edit of one ontology module or shapes graph on the head shown
 * (v1's TBox and ABox editors), committed to this change set; anyone with `propose_change`
 * may, not only its proposer. A parse error is shown with the parser's own words.
 */
export default function TurtleEditor({ review, item, onClose, onChanged }: { review: Review; item: ItemChange; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation();
  const headingId = useId();
  const reference = item.newReference ?? "";
  const load = useCallback((signal: AbortSignal) => getItemVersion(reference, signal), [reference]);
  const version = useLoad(load);
  const submission = useSubmission();
  const [text, setText] = useState<string | null>(null);
  if (version.state.status !== "loaded") return <LoadStatus state={version.state} loadingLabel={t("common.loading")} onRetry={version.reload} compact />;
  const stored = version.state.value;
  const value = text ?? stored.text;
  const blob = reference.split("@")[1] ?? null;
  const save = () => {
    submission.submit(async () => {
      // An edit keeps what the item checks or extends: the model, or the process graph.
      const graph = headerValue(stored.text, "graph") === "process" ? ("process" as const) : ("model" as const);
      const content =
        item.kind === "shapes"
          ? { kind: "shapes" as const, name: item.name, turtle: value, inference: headerValue(stored.text, "inference") === "owl_2_rl" ? ("owl_2_rl" as const) : ("none" as const), graph }
          : { kind: "ontology_module" as const, name: item.name, turtle: value, graph };
      await commitItemEdits(review.changeSet.changeSetId, review.head, [{ operation: "replace", item_id: item.itemId, based_on: blob, content }]);
      onChanged();
      onClose();
    });
  };
  const failure = submission.state.status === "failed" ? submission.state.error : null;
  return (
    <section className="app-card space-y-2 p-3" aria-labelledby={headingId}>
      <h4 id={headingId} className="app-subsection-title text-sm">{t("reviews.curation.edit.title", { name: item.name })}</h4>
      <SourceView label={t("reviews.curation.edit.title", { name: item.name })} language="turtle" value={value} presentation="full" maxHeight={480} onChange={setText} />
      {failure !== null ? <ParseProblem error={failure} /> : null}
      <p className="app-text-muted text-xs">{t("reviews.curation.edit.explain")}</p>
      <div className="flex gap-2">
        <AppButton size="sm" variant="primary" disabled={submission.state.status === "submitting" || value === stored.text} onClick={save}>
          {t("reviews.curation.edit.save")}
        </AppButton>
        <AppButton size="sm" variant="ghost" onClick={onClose}>
          {t("common.cancel")}
        </AppButton>
      </div>
    </section>
  );
}

/** A stored RDF item's header line `# <key>: <value>` (content D-9), or `null`. */
function headerValue(text: string, key: string): string | null {
  const line = text.split("\n").find((entry) => entry.startsWith(`# ${key}: `));
  return line === undefined ? null : line.slice(key.length + 4).trim();
}

function ParseProblem({ error }: { error: UiError }) {
  const problem = error.kind === "problem" ? error.problem.details["problem"] : undefined;
  return (
    <div className="space-y-1">
      <ErrorNotice error={error} compact={true} />
      {typeof problem === "string" ? <p className="app-error-text whitespace-pre-wrap font-mono text-xs">{problem}</p> : null}
    </div>
  );
}
